/**
 * Filter Engine - Generic data filtering system
 *
 * This engine applies filters to any data based on filter definitions.
 * It's completely generic - no hardcoded logic for specific filters.
 */

import { Filter, FilterType, ActiveFilters } from '../types/filters';

/**
 * Main Filter Engine class
 */
export class FilterEngine {
  /**
   * Apply all active filters to a dataset
   */
  static applyFilters<T = any>(
    data: T[],
    activeFilters: ActiveFilters,
    filterDefinitions: Filter[]
  ): T[] {
    if (!data || data.length === 0) {
      return [];
    }

    if (Object.keys(activeFilters).length === 0) {
      return data;
    }

    let filtered = [...data];

    // Apply each active filter
    Object.entries(activeFilters).forEach(([filterId, value]) => {
      const filter = filterDefinitions.find((f) => f.id === filterId);
      if (!filter || !filter.enabled) {
        return;
      }

      // Skip if value is default (no filtering needed)
      if (this.isDefaultValue(value, filter)) {
        return;
      }

      try {
        filtered = this.applyFilter(filtered, filter, value);
      } catch (error) {
        // Silent fail
      }
    });

    return filtered;
  }

  /**
   * Apply a single filter to the dataset
   */
  private static applyFilter<T = any>(
    data: T[],
    filter: Filter,
    value: any
  ): T[] {
    const filterFn = FILTER_FUNCTIONS[filter.type];
    if (!filterFn) {
      return data;
    }

    return data.filter((item) => {
      const itemValue = this.getValueAtPath(item, filter.dataPath);

      // Transform value if transformer is defined
      const transformedValue = filter.transformer
        ? filter.transformer(value)
        : value;

      return filterFn(itemValue, transformedValue, filter.config);
    });
  }

  /**
   * Get nested property value using dot notation
   * e.g., "enrichment.btl_analysis.gross_yield" -> item.enrichment.btl_analysis.gross_yield
   */
  private static getValueAtPath(obj: any, path: string): any {
    if (!obj || !path) return undefined;

    const keys = path.split('.');
    let current = obj;

    for (const key of keys) {
      if (current === null || current === undefined) {
        return undefined;
      }
      current = current[key];
    }

    return current;
  }

  /**
   * Check if a filter value is the default (no filtering needed)
   */
  private static isDefaultValue(value: any, filter: Filter): boolean {
    // For arrays (checkbox, multiselect)
    if (Array.isArray(value) && Array.isArray(filter.defaultValue)) {
      if (value.length === 0) return true;
      const defaultArray = filter.defaultValue as string[];
      if (value.length === defaultArray.length &&
          value.every((v, i) => v === defaultArray[i])) {
        return true;
      }
      return false;
    }

    // For range filters
    if (Array.isArray(value) && value.length === 2) {
      const [min, max] = value;
      const [defaultMin, defaultMax] = filter.defaultValue as [number, number];
      return min === defaultMin && max === defaultMax;
    }

    // For other types
    return value === filter.defaultValue;
  }

  /**
   * Get count of items that would pass filters
   */
  static getFilteredCount(
    data: any[],
    activeFilters: ActiveFilters,
    filterDefinitions: Filter[]
  ): number {
    return this.applyFilters(data, activeFilters, filterDefinitions).length;
  }

  /**
   * Check if any filters are active
   */
  static hasActiveFilters(activeFilters: ActiveFilters, filterDefinitions: Filter[]): boolean {
    return Object.entries(activeFilters).some(([filterId, value]) => {
      const filter = filterDefinitions.find((f) => f.id === filterId);
      return filter && !this.isDefaultValue(value, filter);
    });
  }

  /**
   * Get list of active filter IDs
   */
  static getActiveFilterIds(activeFilters: ActiveFilters, filterDefinitions: Filter[]): string[] {
    return Object.entries(activeFilters)
      .filter(([filterId, value]) => {
        const filter = filterDefinitions.find((f) => f.id === filterId);
        return filter && !this.isDefaultValue(value, filter);
      })
      .map(([filterId]) => filterId);
  }

  /**
   * Clear all filters
   */
  static clearAllFilters(filterDefinitions: Filter[]): ActiveFilters {
    return filterDefinitions.reduce((acc, filter) => {
      acc[filter.id] = filter.defaultValue;
      return acc;
    }, {} as ActiveFilters);
  }

  /**
   * Clear a single filter
   */
  static clearFilter(
    activeFilters: ActiveFilters,
    filterId: string,
    filterDefinitions: Filter[]
  ): ActiveFilters {
    const filter = filterDefinitions.find((f) => f.id === filterId);
    if (!filter) return activeFilters;

    return {
      ...activeFilters,
      [filterId]: filter.defaultValue
    };
  }
}

/**
 * Filter functions for each type
 * Adding a new filter type? Add its logic here
 */
const FILTER_FUNCTIONS: Record<
  FilterType,
  (itemValue: any, filterValue: any, config: any) => boolean
> = {
  /**
   * Range filter - check if value is within [min, max]
   */
  range: (itemValue: any, [min, max]: [number, number]) => {
    if (itemValue === undefined || itemValue === null) return false;

    // Handle percentage strings like "5.2%"
    let numValue: number;
    if (typeof itemValue === 'string') {
      const cleaned: string = itemValue.replace('%', '').trim();
      numValue = Number(cleaned);
    } else {
      numValue = Number(itemValue);
    }

    if (isNaN(numValue)) return false;
    return numValue >= min && numValue <= max;
  },

  /**
   * Checkbox filter - check if value is in selected options
   */
  checkbox: (itemValue: string, selected: string[]) => {
    if (!itemValue) return false;
    if (selected.length === 0) return true; // No filter applied
    return selected.includes(String(itemValue));
  },

  /**
   * Select filter - exact match
   */
  select: (itemValue: string, selectedValue: string) => {
    if (!itemValue) return false;
    return String(itemValue) === selectedValue;
  },

  /**
   * Toggle filter - boolean match
   */
  toggle: (itemValue: boolean, filterValue: boolean) => {
    return Boolean(itemValue) === filterValue;
  },

  /**
   * Multi-select filter - check if item has any of selected values
   */
  multiselect: (itemValue: string | string[], selected: string[]) => {
    if (!itemValue) return false;
    if (selected.length === 0) return true; // No filter applied

    const values = Array.isArray(itemValue) ? itemValue : [itemValue];
    return selected.some((s) => values.includes(s));
  },

  /**
   * Search filter - text contains search term
   */
  search: (itemValue: string, searchTerm: string) => {
    if (!itemValue) return false;
    if (!searchTerm || searchTerm.trim() === '') return true; // No search term

    const searchLower = searchTerm.toLowerCase().trim();
    const valueLower = String(itemValue).toLowerCase();
    return valueLower.includes(searchLower);
  },

  /**
   * Location search filter — server-side matching only.
   *
   * The /properties/search endpoint already classifies and applies the
   * location tokens; by the time results reach this client-side engine,
   * the rows have been pruned to matches. Returning true here keeps the
   * client-side selectors (counts, hasActiveFilters) honest without
   * re-implementing the server's postcode-shape classifier and risking
   * drift between the two. This function is only consulted for legacy
   * client-side filtering paths, which we don't use for the deals list.
   */
  location_search: () => true,
};

/**
 * Helper function to convert filter to human-readable string
 */
export function filterToString(filter: Filter, value: any): string {
  switch (filter.type) {
    case 'range': {
      const [min, max] = value as [number, number];
      const rangeFilter = filter as Filter<'range'>;
      const formatter = rangeFilter.config.format || ((v: number) => `${v}${rangeFilter.config.unit || ''}`);
      return `${formatter(min)} - ${formatter(max)}`;
    }
    case 'checkbox':
    case 'multiselect': {
      const selected = value as string[];
      if (selected.length === 0) return 'All';
      if (selected.length === 1) return selected[0];
      return `${selected.length} selected`;
    }
    case 'select':
      return value as string;
    case 'toggle': {
      const toggleFilter = filter as Filter<'toggle'>;
      return value ? toggleFilter.config.onLabel : toggleFilter.config.offLabel;
    }
    case 'search':
      return value ? `"${value}"` : 'No search';
    default:
      return String(value);
  }
}

/**
 * Helper function to validate filter value
 */
export function validateFilterValue(filter: Filter, value: any): boolean {
  // Use custom validator if provided
  if (filter.validator) {
    return filter.validator(value);
  }

  // Default validation by type
  switch (filter.type) {
    case 'range': {
      if (!Array.isArray(value) || value.length !== 2) return false;
      const [min, max] = value;
      const rangeFilter = filter as Filter<'range'>;
      return (
        typeof min === 'number' &&
        typeof max === 'number' &&
        min >= rangeFilter.config.min &&
        max <= rangeFilter.config.max &&
        min <= max
      );
    }
    case 'checkbox':
    case 'multiselect': {
      if (!Array.isArray(value)) return false;
      const multiFilter = filter as Filter<'checkbox'> | Filter<'multiselect'>;
      const validValues = multiFilter.config.options.map((o: any) => o.value);
      return value.every((v) => validValues.includes(v));
    }
    case 'select': {
      const selectFilter = filter as Filter<'select'>;
      const validValues = selectFilter.config.options.map((o: any) => o.value);
      return validValues.includes(value);
    }
    case 'toggle':
      return typeof value === 'boolean';
    case 'search':
      return typeof value === 'string';
    default:
      return true;
  }
}
